import { TZDateMini } from '@date-fns/tz';
import { startOfDay } from 'date-fns';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useReducer,
  type MutableRefObject,
} from 'react';
// This should be replaced with the Intl Locale weekInfo API (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Locale/weekInfo) as soon as it gets full support. It's currently not supported in Firefox, but there is an open issue for that - https://bugzilla.mozilla.org/show_bug.cgi?id=1693576
// TODO: remove as soon as Firefox gets support
import { getWeekStartByLocale } from 'weekstart';

import { parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import {
  getRegionalFormat,
  getTimezone,
} from '@dynatrace-sdk/user-preferences';

import { useCalendarNavigation } from './useCalendarNavigation.js';
import { TimeValue, TimeframeV2 } from '../../../core/types/time.js';
import { useFormFieldMessagesContext } from '../../forms-core/hooks/useFormFieldMessagesContext.js';
import type { FormFieldMessagesReducerAction } from '../../forms-core/state/formfield-messages-state-reducer.js';
import { isTimeframeV2 } from '../../forms-core/time/types.js';
import type {
  CalendarProps,
  CalendarStrictValueType,
  CalendarType,
} from '../Calendar.js';
import { initialState as defaultState } from '../contexts/CalendarContext.js';
import { reducer } from '../state/calendar-state-reducer.js';
import { validateCalendarSelection } from '../state/calendar-validation.js';
import type {
  CalendarReducerAction,
  CalendarState,
} from '../types/shared-types.js';
import {
  convertToCalendarStructure,
  getTimeOffsetFromInitialValue,
} from '../utils/helpers.js';

//#region types
/** @internal */
export type UseCalendarProps<Type extends CalendarType> = Pick<
  CalendarProps<Type>,
  'max' | 'min' | 'onChange' | 'type' | 'precision'
> & {
  value: CalendarStrictValueType<Type>;
  calendarRef: MutableRefObject<HTMLElement | null>;
};
//#endregion

/**
 * Checks whether the passed value is a Timeframe and checks if the value equals the second timeframe.
 * This check is for range calendars.
 */
function isDifferentTimeframe(
  calendarValue: Partial<TimeframeV2> | TimeValue | null,
  selectedDate: Partial<TimeframeV2> | TimeValue | null,
  type: CalendarType,
) {
  return (
    isTimeframeV2(calendarValue, type) &&
    isTimeframeV2(selectedDate, type) &&
    !(
      calendarValue?.from?.value === selectedDate?.from?.value &&
      calendarValue?.to?.value === selectedDate?.to?.value
    )
  );
}

/**
 * Checks whether the passed value is of type TimeValue and checks if the value equals the second timeframe.
 * This check is for single calendars.
 */
function isDifferentTimeValue(
  calendarValue: Partial<TimeframeV2> | TimeValue | null,
  selectedDate: Partial<TimeframeV2> | TimeValue | null,
  type: CalendarType,
) {
  return (
    !isTimeframeV2(selectedDate, type) &&
    !isTimeframeV2(calendarValue, type) &&
    calendarValue?.value !== selectedDate?.value
  );
}

/**
 * Calendar hook to initialize the calendar state and update it to prop changes.
 * @internal
 */
export function useCalendar<Type extends CalendarType>({
  calendarRef,
  value,
  onChange,
  // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
  // @ts-ignore Strict mode transition - needs to be investigated
  type = 'single',
  min,
  max,
  precision,
}: UseCalendarProps<Type>) {
  const uniqueId = useId();
  // All parse times here need to use the same _now_ date, to not be subject
  // to millisecond differences during parsing.
  const configuredTimezone = getTimezone();
  const relativeNow = TZDateMini.tz(configuredTimezone).getTime();
  const minDate = useMemo(
    () => parseTimeAsTimeValue(min, relativeNow),
    // MinDate should not update every render and the relativeNow would cause that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [min],
  );
  const maxDate = useMemo(
    () => parseTimeAsTimeValue(max, relativeNow),
    // MaxDate should not update every render and the relativeNow would cause that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [max],
  );

  const timeOffset = useMemo(
    () =>
      getTimeOffsetFromInitialValue<Type>(
        value,
        type,
        minDate,
        maxDate,
        precision,
      ),
    // The timeOffset should only derive from the value initially.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const calendarValue = convertToCalendarStructure<Type>(
    timeOffset,
    type,
    value,
    minDate?.absoluteDate,
    maxDate?.absoluteDate,
    precision,
  );

  const activeISODate = isTimeframeV2(calendarValue, type)
    ? calendarValue.from?.absoluteDate
    : calendarValue?.absoluteDate;

  const today = startOfDay(TZDateMini.tz(configuredTimezone));

  const initialState = {
    ...defaultState,
    position: type === 'single' ? undefined : 'from',
    type,
    min: minDate
      ? new TZDateMini(minDate.absoluteDate, configuredTimezone)
      : undefined,
    max: maxDate
      ? new TZDateMini(maxDate.absoluteDate, configuredTimezone)
      : undefined,
    selectedDate: calendarValue,
    activeDate: new TZDateMini(
      activeISODate ?? today.toISOString(),
      configuredTimezone,
    ),
    // If it is initialized with a timeoffset it keeps the offset.
    // This will be customizable in the future.
    timeOffset,
  };

  const { dispatch: formMessageDispatch } = useFormFieldMessagesContext();

  const callback = useCallback(
    (state: CalendarState, action: CalendarReducerAction) =>
      reducer(
        state,
        action,
        (args: FormFieldMessagesReducerAction) => {
          // Delay the ErrorMessage update by a tick to not update the calendar state and the message state at the same time.
          window.setTimeout(() => {
            formMessageDispatch?.(args);
          });
        },
        // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
        // @ts-ignore Strict mode transition - needs to be investigated
        onChange,
        precision,
      ),
    [formMessageDispatch, onChange, precision],
  );

  const [state, dispatch] = useReducer(
    callback,
    // activeDate might be undefined but will be set in the initializer, to ensure the correct timezone.
    initialState as CalendarState,
    (state: CalendarState) => {
      // Make sure the state initializes with a new TZDateMini when the component is created
      // instead of on the module scope.
      // Extract the user-settings timezone to use for the calculation

      const internalError = validateCalendarSelection(
        state.selectedDate,
        state,
        (args: FormFieldMessagesReducerAction) => {
          // Delay the ErrorMessage update by a tick to not update the calendar state and the message state at the same time.
          window.setTimeout(() => {
            formMessageDispatch?.(args);
          });
        },
      );

      return {
        ...state,
        errorId: internalError,
        activeDate: state.activeDate ?? today,
        today,
      };
    },
  );
  // Get localized first day of the week to correctly display the weekday labels
  const region = useMemo(() => getRegionalFormat(), []);

  /** Updates the state in case of a controlled update from the outside. */
  if (
    isDifferentTimeframe(calendarValue, state.selectedDate, type) ||
    isDifferentTimeValue(calendarValue, state.selectedDate, type)
  ) {
    dispatch({
      type: 'valueChanged',
      payload: calendarValue,
    });
  }

  if (type !== state.type) {
    dispatch({
      type: 'typeChanged',
      payload: { type, position: type === 'single' ? undefined : 'from' },
    });
  }

  // The calendar's state needs to be updated when the localization information changes
  useEffect(() => {
    // TODO: remove this dependency as soon as Firefox supports the new Intl.Locale weekInfo API (and use it instead)
    const weekStartsOn = getWeekStartByLocale(region);
    dispatch({
      type: 'weekStartsOnChange',
      payload: weekStartsOn,
    });
  }, [dispatch, region]);

  useCalendarNavigation({
    calendarRef,
    state,
    dispatch,
  });

  return { state, dispatch, uniqueId };
}
